import React, { useEffect, useState } from 'react';
import './EngagementTimeline.scss';
import GanttChartView from './GanttChartView/GanttChartView';
import { useSearchParams } from 'react-router-dom';
import { GanttChartItem } from '../../GanttChartComponent/models/GanttChartItem.model';
import {
    MarketplaceEngagementTimelineSubTab,
    MarketplaceEngagementTimelineSubTabs,
} from '../../../slices/marketplace/timeline/models/enums/seller-marketplace-engagement-timeline-sub-tab.enum';
import ProcessView from './ProcessView/ProcessView';
import { MarketplaceCalendar } from '../../../components/Marketplace/MarketplaceCalendar';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import ReactDOMServer from 'react-dom/server';
import { ChevronDiagramEvent } from '../../AdminMarketplace/ChevronProcessDiagram/models/ChevronDiagramEvent';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { ActionDocumentType } from '../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import {
    NdaTermSelectionOptionsList,
    TimelineActionType,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../slices/marketplace/timeline/models/engagement-data.model';
import TimelineProcessViewLegend from '../../../components/TimelineProcessViewLegend/TimelineProcessViewLegend';
import TimelineEventsPartyLegend from '../../../components/TimelineEventsPartyLegend/TimelineEventsPartyLegend';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { useStateSelector } from '../../../store/selectors';
import { USER_ROLES } from '../../../utils/constants';
import { TimelineAction } from '../../../slices/marketplace/timeline/models/timeline-actions.model';

interface MyEngagementProps {
    id: number;
    events: TimelineEvent[];
    buyerActionState?: EngagementBuyerActionState[];
    engagementData: EngagementData;
    startDate: Date;
    setSearchParams: (params: { [key: string]: string }) => void;
    onEventClick(id: string): void;
    isReadOnly: boolean;
}

const EngagementTimeline = (props: MyEngagementProps) => {
    const [activeTab, setActiveTab] = useState<number>(
        MarketplaceEngagementTimelineSubTab.ProcessView
    );
    const [searchParams] = useSearchParams({});

    const summary = useStateSelector((s) => s.userSummary.summary);

    const getMarketplaceParty = (): TimelineParty => {
        if (summary.isMarketplaceBuyer) {
            return TimelineParty.Buyer;
        }
        if (summary?.roleId === USER_ROLES.ADMIN) {
            return TimelineParty.VetValue;
        }
        return TimelineParty.Seller;
    };

    const tabComponents = [
        {
            id: MarketplaceEngagementTimelineSubTab.ProcessView,
            content: () => (
                <ProcessView
                    timelineEvents={props.events}
                    engagementStartDate={props.startDate}
                    onActionClick={(item: ChevronDiagramEvent) => {
                        props.onEventClick(item.id);
                    }}
                    isEventActionVisible={isEventActionClickable}
                />
            ),
        },
        {
            id: MarketplaceEngagementTimelineSubTab.GanttChartView,
            content: () => (
                <GanttChartView
                    timelineEvents={props.events}
                    buyerActionState={props.buyerActionState}
                    startDate={new Date(props.startDate)}
                    onEventClick={(item: GanttChartItem) => {
                        props.onEventClick(item.id);
                    }}
                    isEventClickable={isEventActionClickable}
                />
            ),
        },
        {
            id: MarketplaceEngagementTimelineSubTab.CalendarView,
            content: () => (
                <MarketplaceCalendar
                    timelineEvents={props.events}
                    startDate={new Date(props.startDate)}
                    timelineId={props.id}
                    onEventClick={props.onEventClick}
                    canUserRedirectToAction={isEventActionClickable}
                />
            ),
        },
    ];

    const isEventActionDocDetailsContainsFile = (docId: string): boolean => {
        const doc = props.engagementData.documentsList.find(
            (doc) => doc.id === docId
        );

        if (doc?.type == ActionDocumentType.ForSign) {
            return true;
        }

        const result = doc?.fileName != '';
        return result;
    };

    const isBuyerReviewDocReady = (action: TimelineAction) => {
        if (action?.actionType !== TimelineActionType.BuyerReviewDocument)
            return false;

        const doc = props.engagementData.documentsList.find(
            (doc) => doc.id === action?.referenceItemId
        );

        const isLinked = !!action?.actionReferenceId;
        const isLinkedReady =
            isLinked &&
            props.events.find(
                (e) =>
                    e.linkedAction &&
                    e.linkedAction?.id == action?.actionReferenceId
            )?.status === TimelineEventStatus.Completed;

        const result = doc?.isPrepared && (!isLinked || isLinkedReady);

        return result;
    };

    const isEventActionClickable = (eventid: string): boolean => {
        if (props.isReadOnly) {
            return false;
        }

        const event = props.events.find((event) => event.id === eventid);
        if (event == null) {
            return false;
        }

        if (!event?.linkedAction) return false;

        const party = getMarketplaceParty();
        const matchingMarty = event.party == party;
        const sellerViewingSeller =
            matchingMarty && party === TimelineParty.Seller;
        const isEventUpcoming = event.status === TimelineEventStatus.Upcoming;

        if (
            event.linkedAction?.actionType ==
            TimelineActionType.SellerEngagementDocument
        ) {
            const canEngagementDocBeViewed =
                party === TimelineParty.VetValue ||
                (sellerViewingSeller &&
                    !isEventUpcoming &&
                    isEventActionDocDetailsContainsFile(
                        event?.linkedAction?.referenceItemId
                    ));
            return canEngagementDocBeViewed;
        }

        if (
            event.linkedAction?.actionType ==
            TimelineActionType.BuyerReviewDocument
        ) {
            const canBuyerReviewDocBeViewed =
                (party === TimelineParty.VetValue ||
                    (party === TimelineParty.Buyer && !isEventUpcoming)) &&
                isBuyerReviewDocReady(event?.linkedAction);

            return canBuyerReviewDocBeViewed;
        }

        if (
            NdaTermSelectionOptionsList.some(
                (option) => option.value === event.linkedAction?.actionType
            )
        ) {
            return party === TimelineParty.VetValue || !isEventUpcoming;
        }

        if (
            event.linkedAction?.actionType ===
            TimelineActionType.SellerTeaserApproval
        ) {
            const canTeaserBeViewed = isEventUpcoming
                ? party === TimelineParty.VetValue
                : party !== TimelineParty.Buyer;

            return canTeaserBeViewed;
        }

        if (
            event.linkedAction?.actionType ===
            TimelineActionType.SellerBuyerSelectionIdNda
        ) {
            if (party === TimelineParty.Seller && !isEventUpcoming) return true;
            const targetAction = props.buyerActionState?.find(
                (p) => p.actionId === event.linkedAction.id
            );
            return !!targetAction;
        }

        return isEventUpcoming
            ? false
            : matchingMarty || party === TimelineParty.VetValue;
    };

    useEffect(() => {
        const value = searchParams.get('subtab');
        const numberValue = Number(value);
        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= tabComponents.length
        ) {
            setActiveTab(numberValue);
        }
        if (numberValue > tabComponents.length) {
            props.setSearchParams({
                subTab: Number(
                    MarketplaceEngagementTimelineSubTab.ProcessView
                ).toString(),
            });
        }
    }, [searchParams]);

    const renderTabContent = () => {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return activeTabData ? activeTabData.content() : <></>;
    };

    const getTabTooltip = (subTabId: MarketplaceEngagementTimelineSubTab) => {
        switch (subTabId) {
            case MarketplaceEngagementTimelineSubTab.CalendarView:
                return (
                    <InfoTooltip
                        idText="calendar-view-info-tooltip"
                        text=""
                        place="bottom"
                        html={ReactDOMServer.renderToString(
                            <TimelineEventsPartyLegend />
                        )}
                    />
                );
            case MarketplaceEngagementTimelineSubTab.GanttChartView:
                return (
                    <InfoTooltip
                        idText="gantt-chart-info-tooltip"
                        text=""
                        place="bottom"
                        html={ReactDOMServer.renderToString(
                            <TimelineEventsPartyLegend />
                        )}
                    />
                );
            case MarketplaceEngagementTimelineSubTab.ProcessView:
                return (
                    <InfoTooltip
                        idText="process-view-tooltip"
                        text=""
                        place="bottom"
                        html={ReactDOMServer.renderToString(
                            <TimelineProcessViewLegend />
                        )}
                    />
                );
            default:
                return <></>;
        }
    };

    const renderTabs = () => {
        return (
            <div
                key={props.events.length}
                className="body_inner_content gantt-chart-container">
                <div className="RRT__container">
                    <div className="RRT__tabs body-tabs">
                        {MarketplaceEngagementTimelineSubTabs?.map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className={
                                        activeTab === item.id
                                            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                            : 'RRT__tab'
                                    }
                                    onClick={() => {
                                        setActiveTab(item.id);
                                        props.setSearchParams({
                                            subtab: item.id.toString(),
                                        });
                                    }}>
                                    {item.content}
                                    {getTabTooltip(item.id)}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        );
    };

    return (
        <div className="engagement-timeline gantt-chart-container">
            {renderTabs()}
        </div>
    );
};

export default EngagementTimeline;
